import React from 'react'
import {
  Grid,
  TextField,
  Typography,
  Divider,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton
} from '@mui/material'
import { Info } from '@mui/icons-material'
import {
  FormInputRow,
  ImageUploader,
  ActionButton
} from '@emerald-works-nova/components'
import ImageDisplay from '../../../../../../../components/image-display'
import { useStyles } from './style'
import { ACTIONS, BANNER_STYLES, getOrdinalSuffix } from '../utils'

const ProductBannersSectionForm = ({ section, onChange, isEdit }) => {
  const classes = useStyles()
  const { cards } = section.data

  const handleBannerChange = (cardOrder, field, value) => {
    const updatedBanners = cards?.map(card =>
      card.order === cardOrder ? { ...card, [field]: value } : card
    ) || []
    onChange(section.order, { ...section, data: { ...section.data, cards: updatedBanners } })
  }

  const addNewBanner = () => {
    const existingBanners = cards || []
    const newId = Math.max(...existingBanners.map(b => b.order), 0) + 1
    const newBanner = {
      order: newId,
      header: '',
      subHeader: '',
      description: '',
      cta: '',
      action: '',
      style: 'default',
      imageInfo: {
        name: '',
        imgUrl: '',
        s3Url: '',
        width: 0,
        height: 0,
        altText: '',
        author: '',
        stock: ''
      }
    }
    onChange(section.order, {
      ...section,
      data: { ...section.data, cards: [...existingBanners, newBanner] }
    })
  }

  const removeBanner = (cardOrder) => {
    const updatedBanners = cards?.filter(card => card.order !== cardOrder) || []
    onChange(section.order, { ...section, data: { ...section.data, cards: updatedBanners } })
  }

  const handleImageUpload = (cardOrder, imgInfo) => {
    handleBannerChange(cardOrder, 'imageInfo', imgInfo)
  }

  const setImageInfo = (cardOrder, updatedImageInfo) => {
    handleBannerChange(cardOrder, 'imageInfo', updatedImageInfo)
  }

  return (
    <Grid container spacing={3} className={classes.formContainer} data-test='product-banners-section-form-container'>
      {cards?.map((card, index) => {
        const hasImage = card.imageInfo?.imgUrl || card.imageInfo?.s3Url

        return (
          <React.Fragment key={card.order}>
            <Grid container spacing={3} className={classes.bannerGrid} data-test={`product-banners-section-form-banner-${index}-container`}>
              <Grid item xs={12}>
                <Box className={classes.bannerContainer}>
                  <Box className={classes.bannerHeader}>
                    <Typography variant='subtitle1' className={classes.bannerTitle} data-test={`product-banners-section-form-banner-${index}-title`}>
                      {index + 1}{getOrdinalSuffix(index + 1)} Banner
                    </Typography>
                    {(cards?.length > 1) && isEdit && (
                      <ActionButton
                        variant='secondary'
                        onClick={() => removeBanner(card.order)}
                        dataTest={`product-banners-section-form-banner-${index}-remove-button`}
                      >
                        Remove
                      </ActionButton>
                    )}
                  </Box>

                  <Grid container spacing={2} className={classes.bannerContent} data-test={`product-banners-section-form-banner-${index}-content-container`}>
                    <Grid item md={12} lg={5}>
                      <Grid container direction='column' spacing={2}>
                        <Grid item data-test={`product-banners-section-form-banner-${index}-header-container`}>
                          <TextField
                            fullWidth
                            label='Header'
                            value={card.header || ''}
                            onChange={(e) => handleBannerChange(card.order, 'header', e.target.value)}
                            variant='outlined'
                            disabled={!isEdit}
                            data-test={`product-banners-section-form-banner-${index}-header-input`}
                          />
                        </Grid>
                        <Grid item data-test={`product-banners-section-form-banner-${index}-subheader-container`}>
                          <TextField
                            fullWidth
                            label='Subheader'
                            value={card.subHeader || ''}
                            onChange={(e) => handleBannerChange(card.order, 'subHeader', e.target.value)}
                            variant='outlined'
                            disabled={!isEdit}
                            data-test={`product-banners-section-form-banner-${index}-subheader-input`}
                          />
                        </Grid>
                        <Grid item data-test={`product-banners-section-form-banner-${index}-cta-container`}>
                          <TextField
                            fullWidth
                            label='CTA Button Text'
                            value={card.cta || ''}
                            onChange={(e) => handleBannerChange(card.order, 'cta', e.target.value)}
                            variant='outlined'
                            disabled={!isEdit}
                            data-test={`product-banners-section-form-banner-${index}-cta-input`}
                          />
                        </Grid>
                        <Grid item data-test={`product-banners-section-form-banner-${index}-action-container`}>
                          <FormControl fullWidth variant='outlined' disabled={!isEdit} required>
                            <InputLabel>CTA Button Action</InputLabel>
                            <Select
                              value={card.action || ''}
                              onChange={(e) => handleBannerChange(card.order, 'action', e.target.value)}
                              label='CTA Button Action'
                              data-test={`product-banners-section-form-banner-${index}-action-select`}
                            >
                              <MenuItem value=''>
                                <em>Select an action</em>
                              </MenuItem>
                              {Object.values(ACTIONS).map((action) => (
                                <MenuItem key={action.value} value={action.value}>
                                  {action.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item data-test={`product-banners-section-form-banner-${index}-style-container`}>
                          <Box display='flex' alignItems='center' gap={1}>
                            <FormControl fullWidth variant='outlined' disabled={!isEdit} required>
                              <InputLabel>Card Style</InputLabel>
                              <Select
                                value={card.style || 'default'}
                                onChange={(e) => handleBannerChange(card.order, 'style', e.target.value)}
                                label='Card Style'
                                data-test={`product-banners-section-form-banner-${index}-style-select`}
                              >
                                {Object.values(BANNER_STYLES).map((style) => (
                                  <MenuItem key={style.value} value={style.value}>
                                    {style.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Tooltip title='This style is only applied to the desktop version' arrow>
                              <IconButton size='small' sx={{ color: 'primary.main' }}>
                                <Info fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item md={12} lg={7} data-test={`product-banners-section-form-banner-${index}-description-container`}>
                      <TextField
                        data-test={`product-banners-section-form-banner-${index}-description-input`}
                        fullWidth
                        label='Description'
                        value={card.description || ''}
                        onChange={(e) => handleBannerChange(card.order, 'description', e.target.value)}
                        variant='outlined'
                        multiline
                        rows={12}
                        disabled={!isEdit}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} className={classes.imageSection} data-test={`product-banners-section-form-banner-${index}-image-container`}>
                    <Grid item xs={12}>
                      <Typography variant='subtitle1' tabIndex={0} className={classes.imageTitle}>
                        Image details
                      </Typography>
                      <Divider className={classes.imageDivider} />
                      {!hasImage ? (
                        <Box mt={2}>
                          <FormInputRow
                            hideTitle
                            description='Upload an image to represent this section visually.'
                            InputComponent={
                              <ImageUploader
                                onDropAccepted={(imgInfo) => handleImageUpload(card.order, imgInfo)}
                                imageNamePrefix={`product-banners-section-form-banner-${index}-`}
                                disabled={!isEdit}
                                validation={{
                                  // TODO: Move these values to constants
                                  maxSize: 10485760, // 10MB
                                  accept: '.jpg,.jpeg,.png,.gif',
                                  minWidth: 400,
                                  minHeight: 400
                                }}
                              />
                            }
                          />
                        </Box>
                      ) : (
                        <Box mt={2}>
                          <ImageDisplay
                            imageInfo={card.imageInfo}
                            setImageInfo={isEdit ? (updatedImageInfo) => setImageInfo(card.order, updatedImageInfo) : () => {}}
                          />
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </React.Fragment>
        )
      })}

      {isEdit && (
        <Grid item xs={12} data-test='product-banners-section-form-add-banner-container'>
          <Box className={classes.addBannerContainer}>
            <Button
              variant='outlined'
              onClick={addNewBanner}
              className={classes.addButton}
              data-test='product-banners-section-form-add-banner-button'
            >
              Add New Banner
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  )
}

export default ProductBannersSectionForm
